'use client'

import { FC, PropsWithChildren, useEffect, useState } from 'react'
import { useUser } from '../../hooks/useUser'
import {
  useAnalytics,
  useAnalyticsHandler,
  useSendAnalyticsEvent,
  type AnalyticsUser,
  type AnalyticsUserField,
} from '@sylveraio/react-utils'
import { usePosthogIdentify } from '../../hooks/usePosthogIdentify'

function useAnalyticsUserDetails(): [boolean, AnalyticsUser] {
  const { data } = useUser()

  usePosthogIdentify(data)

  const [userDetails, setUserDetails] = useState<AnalyticsUser>({
    uid: data?.userId,
    userEmail: data?.email,
    userOrganisation: data?.organisation,
  })

  const [hasValidUserDetails, setHasValidUserDetails] = useState(false)

  // Set the user details object
  useEffect(() => {
    setUserDetails({
      uid: data?.userId,
      userEmail: data?.email,
      userOrganisation: data?.organisation,
    })
  }, [data])

  // Ensure we have a valid user details object
  useEffect(() => {
    const userDetailFields: AnalyticsUserField[] = [
      'uid',
      'userEmail',
      'userOrganisation',
    ]
    setHasValidUserDetails(
      !userDetailFields.some(
        (field) =>
          typeof userDetails[field] === 'undefined' ||
          userDetails[field] === '',
      ),
    )
  }, [userDetails])

  return [hasValidUserDetails, userDetails]
}

export const AnalyticsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { sendAnalyticsEvent } = useSendAnalyticsEvent(useAnalyticsUserDetails)

  useAnalytics(process.env.NEXT_PUBLIC_POSTHOG)

  useAnalyticsHandler(sendAnalyticsEvent)

  return children
}
